import {skipToken} from '@reduxjs/toolkit/query';
import {FlagProps, Parameter} from 'platform/components';

import {isNotNil} from 'ramda';

import {useGetCustomerV2Query} from '@dms/api/customer';
import {useGetRentalOrderQuery} from '@dms/api/rental';
import i18n from '@dms/i18n';
import {rentalOrderRoutes} from '@dms/routes';
import {getCustomerName} from '@dms/shared';

import {buildArray, composePath, getSafely} from 'shared/web';

import {PairedRecordView} from './PairedRecordView';

const LOADING_PLACEHOLDER = '…';

const STATE_COLOR_BY_VALUE = {
  draft: 'neutral',
  reserved: 'blue',
  rented: 'blue',
  returned: 'green',
  closed: 'green',
  canceled: 'red',
} as const;

const resolveStateLabel = (state: string | undefined | null) =>
  isNotNil(state) && state in STATE_COLOR_BY_VALUE
    ? (state as keyof typeof STATE_COLOR_BY_VALUE)
    : null;

export function useRentalOrderPairedRecord(rentalOrderId: string | null): PairedRecordView {
  const {
    data: rentalOrder,
    isLoading,
    isError,
  } = useGetRentalOrderQuery(isNotNil(rentalOrderId) ? {rentalOrderId} : skipToken);

  const {data: customer} = useGetCustomerV2Query(
    isNotNil(rentalOrder?.customerId) ? {customerId: rentalOrder.customerId} : skipToken
  );

  const code =
    rentalOrder?.number ??
    (isLoading ? LOADING_PLACEHOLDER : i18n.t('general.notifications.notFound'));
  const customerName = getCustomerName(customer);

  const stateLabel = resolveStateLabel(rentalOrder?.state);

  const parameters = buildArray<Parameter>()
    .when(isNotNil(customerName), `${i18n.t('entity.customer.labels.customer')}: ${customerName}`)
    .when(
      isNotNil(rentalOrder?.plannedRentalPeriod?.from) &&
        isNotNil(rentalOrder?.plannedRentalPeriod?.to),
      `${rentalOrder?.plannedRentalPeriod?.from} → ${rentalOrder?.plannedRentalPeriod?.to}`
    );

  const flags = buildArray<FlagProps>().when(isNotNil(stateLabel), {
    label: isNotNil(stateLabel) ? i18n.t(`entity.rentalOrder.states.${stateLabel}`) : '',
    colorScheme: isNotNil(stateLabel)
      ? (getSafely(STATE_COLOR_BY_VALUE, stateLabel) ?? 'neutral')
      : 'neutral',
    isSubtle: true,
  });

  return {
    isLoading,
    isError,
    code,
    detailPath: isNotNil(rentalOrderId)
      ? composePath(rentalOrderRoutes.detail, {params: {id: rentalOrderId}})
      : null,
    parameters,
    flags,
    vehicleIds: isNotNil(rentalOrder?.rentalVehicleId) ? [rentalOrder.rentalVehicleId] : [],
  };
}
